import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Lock, Mail, Key, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const PasswordChange = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [smtpConfigured, setSmtpConfigured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Initial, 2: OTP Sent, 3: OTP Verified
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    checkSMTPStatus();
  }, []);

  const checkSMTPStatus = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSMTPStatus();
      setSmtpConfigured(response.data?.smtpConfigured || false);
    } catch (error) {
      setSmtpConfigured(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    try {
      setOtpSending(true);
      const response = await adminAPI.requestOTP(user?.username || 'admin');
      
      if (response.success) {
        if (response.data?.smtpConfigured) {
          setMaskedEmail(response.data?.email || '');
          setStep(2);
          toast({
            title: 'OTP Sent!',
            description: `Verification code sent to ${response.data?.email}`,
          });
        } else {
          setSmtpConfigured(false);
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send OTP',
        variant: 'destructive',
      });
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!formData.otp || formData.otp.length !== 6) {
      toast({
        title: 'Error',
        description: 'Please enter a valid 6-digit OTP',
        variant: 'destructive',
      });
      return;
    }

    try {
      setOtpVerifying(true);
      const response = await adminAPI.verifyOTP(user?.username || 'admin', formData.otp);
      
      if (response.success) {
        setStep(3);
        toast({
          title: 'OTP Verified!',
          description: 'You can now set your new password',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Invalid OTP',
        variant: 'destructive',
      });
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleChangePassword = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    if (formData.newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive',
      });
      return;
    }

    try {
      setPasswordChanging(true);
      
      const payload = {
        username: user?.username || 'admin',
        new_password: formData.newPassword,
      };

      if (smtpConfigured) {
        payload.otp = formData.otp;
      } else {
        if (!formData.currentPassword) {
          toast({
            title: 'Error',
            description: 'Current password is required',
            variant: 'destructive',
          });
          setPasswordChanging(false);
          return;
        }
        payload.current_password = formData.currentPassword;
      }

      const response = await adminAPI.changePassword(payload);
      
      if (response.success) {
        toast({
          title: 'Success!',
          description: 'Password changed successfully',
        });
        // Reset form
        setFormData({
          currentPassword: '',
          otp: '',
          newPassword: '',
          confirmPassword: '',
        });
        setStep(1);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to change password',
        variant: 'destructive',
      });
    } finally {
      setPasswordChanging(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-orange-500" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-600/20 flex items-center justify-center">
          <Lock size={20} className="text-orange-500" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Change Password</h3>
          <p className="text-sm text-gray-400">
            {smtpConfigured 
              ? 'Verify your email with OTP to change password' 
              : 'Enter your current password to change it'}
          </p>
        </div>
      </div>

      {/* SMTP Method - With OTP */}
      {smtpConfigured && (
        <div className="space-y-4">
          {/* Step 1: Request OTP */}
          {step === 1 && (
            <div className="p-4 bg-zinc-900/50 rounded-xl border border-orange-500/10">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="text-orange-500" size={20} />
                <span className="text-gray-300">We'll send a verification code to your registered email</span>
              </div>
              <Button
                onClick={handleRequestOTP}
                disabled={otpSending}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
              >
                {otpSending ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={18} />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2" size={18} />
                    Send Verification Code
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Step 2: Verify OTP */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20 flex items-center gap-3">
                <CheckCircle2 className="text-green-500" size={20} />
                <span className="text-green-400">OTP sent to {maskedEmail}</span>
              </div>
              
              <div>
                <Label className="text-gray-300">Enter 6-digit OTP</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="text"
                    maxLength={6}
                    placeholder="000000"
                    value={formData.otp}
                    onChange={(e) => setFormData({ ...formData, otp: e.target.value.replace(/\D/g, '') })}
                    className="bg-zinc-900 border-orange-500/20 text-white text-center text-2xl tracking-widest font-mono"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleVerifyOTP}
                  disabled={otpVerifying || formData.otp.length !== 6}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
                >
                  {otpVerifying ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2" size={18} />
                      Verify OTP
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleRequestOTP}
                  disabled={otpSending}
                  variant="outline"
                  className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
                >
                  Resend OTP
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Set New Password */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20 flex items-center gap-3">
                <CheckCircle2 className="text-green-500" size={20} />
                <span className="text-green-400">Email verified! Set your new password below.</span>
              </div>
              
              <div>
                <Label className="text-gray-300">New Password</Label>
                <div className="relative mt-2">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    className="bg-zinc-900 border-orange-500/20 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Confirm New Password</Label>
                <div className="relative mt-2">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="bg-zinc-900 border-orange-500/20 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                onClick={handleChangePassword}
                disabled={passwordChanging || !formData.newPassword || !formData.confirmPassword}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
              >
                {passwordChanging ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={18} />
                    Changing Password...
                  </>
                ) : (
                  <>
                    <Key className="mr-2" size={18} />
                    Change Password
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Non-SMTP Method - Current Password Required */}
      {!smtpConfigured && (
        <div className="space-y-4">
          <div className="p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20 flex items-start gap-3">
            <AlertCircle className="text-yellow-500 mt-0.5" size={20} />
            <div>
              <span className="text-yellow-400 font-medium">Email verification not available</span>
              <p className="text-gray-400 text-sm mt-1">
                SMTP is not configured. Enter your current password to change it.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-gray-300">Current Password</Label>
            <div className="relative mt-2">
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                placeholder="Enter current password"
                value={formData.currentPassword}
                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                className="bg-zinc-900 border-orange-500/20 text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-gray-300">New Password</Label>
            <div className="relative mt-2">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                className="bg-zinc-900 border-orange-500/20 text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-gray-300">Confirm New Password</Label>
            <div className="relative mt-2">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="bg-zinc-900 border-orange-500/20 text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <Button
            onClick={handleChangePassword}
            disabled={passwordChanging || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
          >
            {passwordChanging ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={18} />
                Changing Password...
              </>
            ) : (
              <>
                <Key className="mr-2" size={18} />
                Change Password
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PasswordChange;
